<script setup>
import {useSquashLevels} from "~/composables/useSquashLevels";
import TournamentStageMatrix from './TournamentStageMatrix.vue';
import BracketKnockoutStage from './BracketKnockoutStage.vue';
import BracketHierarchical from './BracketHierarchical.vue';
import TournamentTeamsTab from './TournamentTeamsTab.vue';
import {computed, ref, onMounted, watch} from 'vue';
import {useTournamentMatrix} from '~/composables/useTournamentMatrix';
import {useHandicapSettingsFor} from '~/composables/useHandicapSettingsFor';

const {transformStageForMatrix} = useTournamentMatrix();
const route = useRoute();
const router = useRouter();

const props = defineProps({
  tournament: {
    type: Object,
    required: true
  },
  knockoutTeamIds: {
    type: Array,
    default: () => []
  },
  // The bracket has no auth of its own; the page knows whether the viewer runs
  // the club, and only the empty state needs it.
  isAdmin: {
    type: Boolean,
    default: false
  }
});

// Team handicaps beside every name in the group tables (labels from the tournament's settings).
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => props.tournament?.id ? {tournamentId: props.tournament.id} : null),
    computed(() => props.tournament?.handicapsEnabled === true));

// A singles tournament (1-a-side) lists players, not teams.
const singles = computed(() => (props.tournament?.playersPerTeam ?? 1) <= 1);
const unitPlural = computed(() => singles.value ? 'players' : 'teams');

// Over already: a finished tournament nobody entered is not "still being set up".
const tournamentEnded = computed(() => {
  const end = props.tournament?.endDate;
  return !!end && new Date(end) < new Date();
});


// Knockout-only view controls — owned here since they only relate to the
// knockout tab (rendered under that tab, not in the page's action strip).
const useHierarchicalView = ref(true);
const fullscreen = defineModel('fullscreen', {type: Boolean, default: false});

// Create reactive refs for stages (so they can be mutated by TournamentStageMatrix)
const {sportUsesSquashLevels} = useSquashLevels();

const groupStages = ref([]);
const knockoutStages = ref([]);

// Watch for changes in tournament stages and update our refs
watch(() => props.tournament.stages, (newStages) => {
  if (!newStages) {
    groupStages.value = [];
    knockoutStages.value = [];
    return;
  }

  groupStages.value = newStages
      .filter(s => s.type === 1)
      .sort((a, b) => a.sort - b.sort)
      .map(stage => transformStageForMatrix(stage, props.tournament))
      .filter(Boolean);

  knockoutStages.value = newStages
      .filter(s => s.type === 2 || s.type === 3)
      .sort((a, b) => {
        // Sort by round (descending) then by sort
        if (a.round !== b.round) {
          return (b.round || 0) - (a.round || 0);
        }
        return a.sort - b.sort;
      });
}, {immediate: true, deep: true});

const hasGroups = computed(() => groupStages.value.length > 0);
const hasKnockout = computed(() => knockoutStages.value.length > 0);
const hasTeams = computed(() => (props.tournament?.teams?.length ?? 0) > 0);

// Internal state for active tab, synced with URL
const activeTabState = ref(route.query.tab || 'groups');

// Active tab for switching between group and knockout views, synced with URL
const activeTab = computed({
  get() {
    // Return current URL tab if valid
    const urlTab = route.query.tab;
    if (urlTab === 'teams' && hasTeams.value) return 'teams';
    if (urlTab === 'groups' && hasGroups.value) return 'groups';
    if (urlTab === 'knockout' && hasKnockout.value) return 'knockout';

    // Default: a stage view if one exists, otherwise the teams list
    return hasKnockout.value ? 'knockout' : hasGroups.value ? 'groups' : 'teams';
  },
  set(newTab) {
    if (newTab === activeTabState.value) return;
    activeTabState.value = newTab;

    // Update URL using replace to avoid adding to browser history
    router.replace({
      query: { ...route.query, tab: newTab }
    }).catch(() => {});
  }
});

// Watch for URL changes (browser back/forward navigation)
watch(() => route.query.tab, (newTab) => {
  if (newTab && (newTab === 'teams' || newTab === 'groups' || newTab === 'knockout')) {
    activeTabState.value = newTab;
  }
});

// Calculate rounds for each knockout stage
const knockoutStagesWithRounds = computed(() => {
  if (!hasKnockout.value || knockoutStages.value.length === 0) return [];

  const stages = knockoutStages.value;
  const maxRound = Math.max(...stages.map(s => s.round || 1));

  return stages.map(stage => ({
    ...stage,
    round: stage.round || 1,
    totalRounds: maxRound
  }));
});

// Calculate third place playoff stage
const thirdPlaceStage = computed(() => {
  return knockoutStagesWithRounds.value.find(s => s.type === 3);
});

// Get main knockout stages (excluding third place)
const mainKnockoutStages = computed(() => {
  return knockoutStagesWithRounds.value.filter(s => s.type !== 3);
});

// Initialize tab based on URL or default to knockout if available
onMounted(() => {
  const urlTab = route.query.tab;

  // Validate URL tab parameter
  if (urlTab === 'teams' && hasTeams.value) {
    activeTabState.value = 'teams';
  } else if (urlTab === 'groups' && hasGroups.value) {
    activeTabState.value = 'groups';
  } else if (urlTab === 'knockout' && hasKnockout.value) {
    activeTabState.value = 'knockout';
  } else {
    // No valid URL param: default to a stage view if one exists, otherwise teams
    const defaultTab = hasKnockout.value ? 'knockout' : hasGroups.value ? 'groups' : 'teams';
    activeTabState.value = defaultTab;

    // Update URL to reflect default tab
    router.replace({
      query: { ...route.query, tab: defaultTab }
    }).catch(() => {});
  }
});
</script>

<template>
  <div class="w-100 position-relative">
    <!-- Nobody has entered: one flat sentence in a tinted box told a member
         nothing about why, and an admin nothing about what to do next. -->
    <competitions-empty-state
        v-if="!hasTeams && !hasGroups && !hasKnockout"
        entity-type-name="tournament"
        :noun="unitPlural"
        icon="mdi-trophy-outline"
        :is-admin="isAdmin"
        :ended="tournamentEnded">
      <v-btn color="primary" variant="flat" rounded="pill" size="small"
             :to="{name: 'ClubTournamentTeams', params: {clubId: tournament.clubId, tournamentId: tournament.id}}">
        Add {{ unitPlural }}
      </v-btn>
    </competitions-empty-state>

    <div v-else>
      <!-- Tabs: Teams · Groups · Knockout (Flutter order). Teams shows once there
           are entrants; Groups/Knockout appear only once those stages exist. -->
      <div v-if="hasTeams" class="tl-seg mb-4">
        <button
            class="tl-seg__opt"
            :class="{ 'tl-seg__opt--active': activeTab === 'teams' }"
            @click="activeTab = 'teams'"
        >
          <v-icon size="15" icon="mdi-account-group"/>
          {{ singles ? 'Players' : 'Teams' }}
        </button>
        <button
            v-if="hasGroups"
            class="tl-seg__opt"
            :class="{ 'tl-seg__opt--active': activeTab === 'groups' }"
            @click="activeTab = 'groups'"
        >
          <v-icon size="15" icon="mdi-table"/>
          Groups
        </button>
        <button
            v-if="hasKnockout"
            class="tl-seg__opt"
            :class="{ 'tl-seg__opt--active': activeTab === 'knockout' }"
            @click="activeTab = 'knockout'"
        >
          <v-icon size="15" icon="mdi-tournament"/>
          Knockout
        </button>
        <button
            v-else-if="hasGroups"
            class="tl-seg__opt tl-seg__opt--disabled"
        >
          <v-icon size="15" icon="mdi-tournament"/>
          Knockout
          <v-tooltip activator="parent" location="bottom">
            Knockout rounds will be available once group stages are complete
          </v-tooltip>
        </button>
      </div>

      <!-- Knockout view controls — only under the Knockout tab, since they only
           apply to the bracket view. -->
      <div v-if="activeTab === 'knockout' && hasKnockout" class="bracket-controls mb-3">
        <div class="view-toggle">
          <button class="as-vt" :class="{ active: !useHierarchicalView }" @click="useHierarchicalView = false">
            <v-icon size="14">mdi-view-list</v-icon>
            Stacked
          </button>
          <button class="as-vt" :class="{ active: useHierarchicalView }" @click="useHierarchicalView = true">
            <v-icon size="14">mdi-tournament</v-icon>
            Hierarchical
          </button>
        </div>
        <button v-if="useHierarchicalView" class="bracket-fs-btn" @click="fullscreen = true">
          <v-icon size="15">mdi-fullscreen</v-icon>
          Fullscreen
        </button>
      </div>

      <div class="overflow-x-auto overflow-y-hidden">
        <!-- Teams Tab Content -->
        <tournament-teams-tab v-if="activeTab === 'teams'" :tournament="tournament"/>

        <!-- Group Stages Tab Content -->
        <div v-else-if="activeTab === 'groups' && hasGroups" style="min-height: 400px;">
          <div class="d-flex flex-column ga-4">
            <div v-for="(stage, index) in groupStages" :key="stage.id" class="group-stage-section tl-card">
              <!-- Group Title -->
              <div class="d-flex align-center justify-space-between group-stage-head">
                <h4 class="group-stage-name">{{ stage.name }}</h4>
                <span v-if="stage.advanceCount" class="tl-chip tl-chip--ok advance-chip">
                  Top {{ stage.advanceCount }} advance
                </span>
              </div>

              <!-- Group Matrix -->
              <tournament-stage-matrix
                  v-model="groupStages[index]"
                  :closed="false"
                  :knockout-team-ids="knockoutTeamIds"
                  :show-squash-level="sportUsesSquashLevels(tournament?.sport)"
                  :handicap-settings="handicapSettings"
              />
            </div>
          </div>
        </div>

        <!-- Knockout Tab Content -->
        <div v-else-if="activeTab === 'knockout' && hasKnockout" class="tl-card d-flex ga-20" style="min-height: 400px;">
          <!-- Hierarchical Knockout Bracket -->
          <!-- min-width:0 lets this flex child shrink to the card, so the bracket's
               own overflow-x scrollbar engages instead of blowing out the layout -->
          <div v-if="useHierarchicalView" class="flex-fill" style="min-width: 0">
            <bracket-hierarchical
                :stages="knockoutStages"
                :allow-editing="true"
                v-model:fullscreen="fullscreen"
            />
          </div>

          <!-- Stacked Knockout Bracket -->
          <div v-else class="d-flex pa-3">
            <bracket-knockout-stage
                class="pa-2"
                v-for="stage in mainKnockoutStages"
                :key="stage.id"
                :stage="stage"
                :round="stage.round"
                :total-rounds="stage.totalRounds"
            />
            <bracket-knockout-stage
                class="pa-2"
                v-if="thirdPlaceStage"
                :key="thirdPlaceStage.id"
                :stage="thirdPlaceStage"
                :round="thirdPlaceStage.round"
                :total-rounds="thirdPlaceStage.totalRounds"
            />
          </div>
        </div>

        <!-- Empty state when no knockout yet -->
        <div v-else-if="activeTab === 'knockout' && !hasKnockout" class="empty-knockout-state tl-card">
          <v-icon size="64" color="textSubtle">mdi-trophy-outline</v-icon>
          <p class="text-h6" style="color: rgb(var(--v-theme-charcoal));">Knockout Rounds Not Available</p>
          <p class="text-body-2" style="color: rgb(var(--v-theme-textSubtle));">Complete group stages to unlock knockout rounds</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* tabs use the global .tl-seg primitive (app/assets/styles/settings.scss) */

/* Knockout-only view controls (Stacked/Hierarchical toggle + Fullscreen) */
.bracket-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.view-toggle {
  display: flex;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 9px;
  padding: 2px;
}

.view-toggle .as-vt {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  padding: 4px 9px;
  border-radius: 7px;
  background: none;
  border: none;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;

  &.active {
    background: rgb(var(--v-theme-charcoal));
    color: #fff;
  }
}

.bracket-fs-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 9px;
  padding: 6px 12px;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

.group-stage-head {
  padding: 14px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.group-stage-name {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.advance-chip {
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
  border-color: rgb(var(--v-theme-borderLight));
}

.empty-knockout-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 48px;
  text-align: center;
  gap: 16px;
}

:deep(.overflow-x-auto) {
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-on-surface), 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.3);
    }
  }
}
</style>
